/**************************************************/
/*  shape_pattern_with_grid_animator.js           */
/**************************************************/
import { LGraphNode, LiteGraph } from "litegraph.js";
import { v4 as uuidv4 } from 'uuid';
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * ShapePatternNode
 * ----------------
 * Generates a pattern array with shape-based tessellation:
 *  - patternName for raw "normDelay" distribution
 *  - shapeType for layout (square, circle, triangle, hex, custom)
 *  - optionally a custom canvas/image for shape drawing
 */
export class ShapePatternNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Shape Pattern";

    if (this.id === -1 || this.id == null) {
      this.id = uuidv4();
    }

    // Default properties
    this.properties = {
      gridCount: parseInt(this.getUrlParam("gridCount")) || 150,
      cellScale: parseFloat(this.getUrlParam("cellScale")) || 1,
      patternName: this.getUrlParam("patternName") || "random",
      shapeType: this.getUrlParam("shapeType") || "square"
    };

    // Internal cache
    this._cachedPattern = null;
    this._lastSig = null;

    // Regular input
    this.addInput("ShapeCanvas", "canvas,image");

    // Output
    this.addOutput("Pattern", "array");

    // Non-drivable combo widgets
    this.addWidget("combo", "Pattern", this.properties.patternName, (val) => {
      this.properties.patternName = val;
      this.updateUrlParam("patternName", val);
    }, {
      values: [
        "verticalWave", "horizontalWave", "diagonalWave", "spiral", "ripples", "columns",
        "rows", "checkerboard", "concentricSquares", "zigzag", "random"
      ]
    });

    this.addWidget("combo", "Shape", this.properties.shapeType, (val) => {
      this.properties.shapeType = val;
      this.updateUrlParam("shapeType", val);
    }, {
      values: ["square", "triangle", "circle", "wiggly-circle", "hexagon", "brush-stroke", "tapered-stroke","scribbly-stroke", "soft-brush", 'rounded-brush', 'warp-brush', 'expressive-brush', "ascii-art", "custom"]
    });

    // Drivable sliders — input dots sit on each slider
    addDrivableSlider(this, "Cells per Side", "gridCount", this.properties.gridCount, 10, 200, 1, (val) => {
      this.updateUrlParam("gridCount", val);
    });

    addDrivableSlider(this, "Cell Scale", "cellScale", this.properties.cellScale, 0.5, 10, 0.1, (val) => {
      this.updateUrlParam("cellScale", Math.round(val * 10) / 10);
    });

    // Position drivable inputs at their widgets (1 visible input: ShapeCanvas)
    finalizeDrivableSliders(this, 1);

    this.size = [200, 120];
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  updateUrlParam() {}

  onExecute() {
    // Sync drivable sliders (Grid Count / Cell Scale animate when driven)
    syncDrivableSliders(this);

    // If "custom" shape is chosen, see if there's a canvas/image connected
    const customCanvas =
      this.properties.shapeType === "custom"
        ? this.getInputData(0)
        : null;

    let data = false;

    if(customCanvas){
      data = customCanvas.toDataURL();
    }

    // Build a signature to see if anything changed
    const propsSig = JSON.stringify({
      d: data,
      c: this.properties.gridCount,
      p: this.properties.patternName,
      s: this.properties.cellScale,
      cs: this.properties.shapeType,
      hasCustom: !!customCanvas
    });

    if (this._lastSig !== propsSig) {
      this._lastSig = propsSig;
      this._cachedPattern = this.buildNormalizedPattern(customCanvas);
    }

    this.setOutputData(0, this._cachedPattern);

  }

  /**
   * Main method that:
   * 1) Computes raw pattern values for each cell
   * 2) Normalizes them into [0..1]
   * 3) Computes shape tessellation (x,y) (top-left corners)
   * 4) Merges into final array, sorted by normDelay
   */
  buildNormalizedPattern(customCanvas) {

    const { gridCount, patternName, shapeType } = this.properties;

    // 1) Compute raw pattern values
    const rawCells = this.computeRawPatternValues(gridCount, patternName);

    // 2) Normalize them into [0..1]
    const normalized = this.normalizePattern(rawCells, gridCount);

    // 3) Compute top-left positions for each shape
    const positions = this.computeShapePositions(gridCount, shapeType);

    // 4) Merge row-major (assuming rawCells is row-major [j,i])
    //    We'll match the index in rawCells to positions by order of creation
    const merged = normalized.map((cell, index) => {
      const { x, y } = positions[index] || { x: 0, y: 0 };

      return {
        i: cell.i,
        j: cell.j,
        normDelay: cell.normDelay,
        scale:  this.properties.cellScale,
        totalCount: cell.totalCount,
        shape: shapeType,
        x,
        y,
        customCanvas: shapeType === "custom" ? customCanvas : null
      };
    });

    // Sort by normDelay if you want strictly ascending pattern order
    merged.sort((a, b) => a.normDelay - b.normDelay);
    return merged;
  }

  /**
   * Creates raw "factor" for each cell, returning
   * an array of { i, j, raw } in row-major order (j outer).
   */
  computeRawPatternValues(gridCount, patternName) {
    const patterns = {
      verticalWave: (i, j) => j * 0.1,
      horizontalWave: (i, j) => i * 0.1,
      diagonalWave: (i, j) => (i + j) * 0.1,
      spiral: (i, j) => Math.sqrt(i * i + j * j) * 0.1,
      ripples: (i, j) =>
        Math.sin(
          Math.sqrt(
            (i - gridCount / 2) ** 2 + (j - gridCount / 2) ** 2
          ) * 2
        ) * 3,
      columns: (i, j) => (i % 2 === 0 ? 1 : 2),
      rows: (i, j) => (j % 2 === 0 ? 1 : 2),
      checkerboard: (i, j) =>
        0.5 + ((i + j) % 2 === 0 ? 0 : 0.5),
      concentricSquares: (i, j) =>
        Math.min(i, j, gridCount - i - 1, gridCount - j - 1) * 0.1,
      zigzag: (i, j) =>
        ((i + (j % 2 === 0 ? 0 : gridCount - 1 - 2 * i)) % gridCount) *
        0.2,
      random: (i, j) => Math.random() * 2
    };

    const currentPattern = patterns[patternName] || patterns.random;

    const rawCells = [];
    for (let j = 0; j < gridCount; j++) {
      for (let i = 0; i < gridCount; i++) {
        const val = currentPattern(i, j);
        rawCells.push({ i, j, raw: val });
      }
    }
    return rawCells;
  }

  /**
   * Normalize raw pattern values to [0..1],
   * returning { i, j, normDelay, totalCount } for each cell.
   */
  normalizePattern(rawCells, gridCount) {
    let minVal = Infinity;
    let maxVal = -Infinity;
    for (const cell of rawCells) {
      if (cell.raw < minVal) minVal = cell.raw;
      if (cell.raw > maxVal) maxVal = cell.raw;
    }

    // If pattern is flat, avoid divide-by-zero
    if (Math.abs(maxVal - minVal) < 1e-6) {
      return rawCells.map((c) => ({
        i: c.i,
        j: c.j,
        normDelay: 0,
        scale: this.properties.cellScale,
        totalCount: gridCount
      }));
    }

    const range = maxVal - minVal;
    return rawCells.map((c) => ({
      i: c.i,
      j: c.j,
      normDelay: (c.raw - minVal) / range, // 0..1
      scale: this.properties.cellScale,
      totalCount: gridCount
    }));
  }

  /**
   * Tesselation: returns an array of { x, y } for each cell in row-major order.
   * x, y are top-left corners in [0..1].
   */
  computeShapePositions(gridCount, shapeType) {
    const positions = [];

    switch (shapeType) {
      case "triangle": {

        const side = 1 / gridCount;
        const vSpacing = side * Math.sqrt(3) / 2;

        for (let row = 0; row < gridCount; row++) {
          const y = row * vSpacing;
          // offset in x if row is odd
          const xOffset = (row % 2) ? side / 2 : 0;

          for (let col = 0; col < gridCount; col++) {
            const x = col * side + xOffset;
            positions.push({ x, y });
          }

        }

        break;
      }

      case "custom":{

        const side = 1 / gridCount;
        const vSpacing = side * Math.sqrt(3) / 2;

        for (let row = 0; row < gridCount; row++) {
          const y = row * vSpacing;
          // offset in x if row is odd
          const xOffset = (row % 2) ? side / 2 : 0;

          for (let col = 0; col < gridCount; col++) {
            const x = col * side + xOffset;
            positions.push({ x, y });
          }
        }
        break;
      }

      case "hexagon": {

        const side = 1 / gridCount;
        const vSpacing = side * Math.sqrt(3) / 2;

        for (let row = 0; row < gridCount; row++) {
          const y = row * vSpacing;
          // offset in x if row is odd
          const xOffset = (row % 2) ? side / 2 : 0;

          for (let col = 0; col < gridCount; col++) {
            const x = col * side + xOffset;
            positions.push({ x, y });
          }
        }
        break;
      }

      case "circle":
      case "square":
      default: {
        // Standard NxN grid, top-left corners
        const cellW = 1 / gridCount;
        const cellH = 1 / gridCount;
        for (let j = 0; j < gridCount; j++) {
          for (let i = 0; i < gridCount; i++) {
            const x = i * cellW;
            const y = j * cellH;
            positions.push({ x, y });
          }
        }
        break;
      }
    }

    return positions;
  }
}

// Register so LiteGraph knows this node
LiteGraph.registerNodeType("patterns/ShapePatternNode", ShapePatternNode);
